import React, { useState, useEffect } from 'react'
import {
 Plus,
 Search,
 Gift,
 FileText,
 ArrowLeft
} from 'lucide-react'
import {
 Card,
 CardHeader,
 CardTitle,
 CardContent,
 AlertMessage,
 Toast,
 ToastType,
 ModernInput,
 ModernButton,
 StatsCard,
 ListPageSkeleton,
} from '../../components/ui'
import { BenefitForm, BenefitsListView } from '../../components/financial'
import { Benefit, BenefitFormData } from '../../types'
import { useBenefits } from '../../hooks/useBenefits'
import { formatCurrency } from '../../utils/formatters'

type View = 'list' | 'form'

export const BenefitsPage: React.FC = () => {
 const { benefits, isLoading, error, pagination, fetchBenefits, createBenefit, updateBenefit, deleteBenefit } = useBenefits()

 const [view, setView] = useState<View>('list')
 const [searchTerm, setSearchTerm] = useState('')
 const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null)
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

 useEffect(() => {
 const params: any = { page: 1, limit: 50 }
 if (searchTerm) params.search = searchTerm
 const timer = setTimeout(() => fetchBenefits(params), searchTerm ? 500 : 0)
 return () => clearTimeout(timer)
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [searchTerm])

 const handleOpenForm = (benefit?: Benefit) => {
 setSelectedBenefit(benefit || null)
 setView('form')
 }

 const handleBack = () => {
 setView('list')
 setSelectedBenefit(null)
 }

 const handleSubmit = async (data: BenefitFormData) => {
 setIsSubmitting(true)
 try {
 if (selectedBenefit) {
 await updateBenefit(selectedBenefit.id, data)
 setToast({ message: 'Benefício atualizado com sucesso!', type: 'success' })
 } else {
 await createBenefit(data)
 setToast({ message: 'Benefício criado com sucesso!', type: 'success' })
 }
 handleBack()
 } catch (err) {
 console.error('Erro ao salvar benefício:', err)
 setToast({ message: 'Erro ao salvar benefício. Tente novamente.', type: 'error' })
 } finally {
 setIsSubmitting(false)
 }
 }

 const handleDelete = async (id: string) => {
 if (confirm('Tem certeza que deseja excluir este benefício?')) {
 try {
 await deleteBenefit(id)
 setToast({ message: 'Benefício excluído com sucesso!', type: 'success' })
 } catch (err) {
 console.error('Erro ao deletar benefício:', err)
 setToast({ message: 'Erro ao deletar benefício. Tente novamente.', type: 'error' })
 }
 }
 }

 // ── FORM VIEW ──────────────────────────────────────────────────────────────
 if (view === 'form') {
 return (
 <div className="space-y-6">
 <div className="flex items-center gap-4">
 <ModernButton variant="outline"size="sm"onClick={handleBack}>
 <ArrowLeft className="h-4 w-4 mr-1"/>
 Voltar
 </ModernButton>
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">
 {selectedBenefit ? 'Editar Benefício' : 'Novo Benefício'}
 </h1>
 <p className="text-sm text-muted-foreground mt-0.5">
 {selectedBenefit ? `Editando: ${selectedBenefit.name}` : 'Preencha os dados do novo benefício'}
 </p>
 </div>
 </div>

 <BenefitForm
 benefit={selectedBenefit}
 onSubmit={handleSubmit}
 onCancel={handleBack}
 isSubmitting={isSubmitting}
 />

 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
 )}
 </div>
 )
 }

 // ── LIST VIEW ──────────────────────────────────────────────────────────────
 return (
 <div className="space-y-6">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold text-foreground font-display">Benefícios</h1>
 <p className="mt-1 text-sm text-muted-foreground">
 Gerencie os benefícios oferecidos nas propostas
 </p>
 </div>
 <div className="mt-4 sm:mt-0">
 <ModernButton onClick={() => handleOpenForm()}>
 <Plus className="h-4 w-4 mr-2"/>
 Novo Benefício
 </ModernButton>
 </div>
 </div>

 {error && <AlertMessage variant="error" message={error} />}
 {isLoading ? (
 <ListPageSkeleton
 variant="table"
 rows={6}
 showFilters={true}
 showStats={true}
 statsCount={2}
 />
 ) : (
 <>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <StatsCard title="Total de Benefícios" value={benefits.length} icon={FileText} iconColor="text-blue-600" />
 <StatsCard
 title="Valor Total"
 value={formatCurrency(benefits.reduce((sum, b) => sum + b.value, 0))}
 icon={Gift}
 iconColor="text-green-600"
 />
 </div>

 <Card>
 <CardContent className="p-6">
 <div className="flex-1">
 <ModernInput
 placeholder="Pesquisar benefícios..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 leftIcon={<Search className="h-4 w-4" />}
 />
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>Lista de Benefícios ({pagination.total})</CardTitle>
 {pagination.totalPages > 1 && (
 <div className="text-sm text-muted-foreground">
 Página {pagination.page} de {pagination.totalPages}
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent>
 <BenefitsListView
 benefits={benefits}
 onEditBenefit={handleOpenForm}
 onDeleteBenefit={handleDelete}
 />

 {pagination.totalPages > 1 && (
 <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
 <div className="text-sm text-muted-foreground">
 Mostrando {benefits.length} de {pagination.total} benefícios
 </div>
 <div className="flex gap-2">
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => fetchBenefits({ page: pagination.page - 1, limit: 50, ...(searchTerm && { search: searchTerm }) })}
 disabled={pagination.page === 1}
 >
 Anterior
 </ModernButton>
 <ModernButton
 variant="outline"
 size="sm"
 onClick={() => fetchBenefits({ page: pagination.page + 1, limit: 50, ...(searchTerm && { search: searchTerm }) })}
 disabled={pagination.page === pagination.totalPages}
 >
 Próxima
 </ModernButton>
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 </>
 )}

 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
 )}
 </div>
 )
}
